<template>
    <b-card no-body class="dashboard-criticality-card mb-0">
        <div class="dashboard-criticality-card__header">
            <div class="dashboard-criticality-card__title-wrap">
                <h4 class="dashboard-criticality-card__title" :title="title">{{ title }}</h4>
                <b-badge variant="light-primary" class="dashboard-criticality-card__count">{{ filteredItems.length }}</b-badge>
                <b-badge v-if="hiddenByFilterCount" variant="light-warning" class="dashboard-criticality-card__count ml-25" v-b-tooltip.noninteractive.hover.bottom title="Kritiklik filtresi nedeniyle gizlenen kayıtlar">
                    <feather-icon icon="FilterIcon" size="10" class="mr-25" />{{ hiddenByFilterCount }}
                </b-badge>
                <b-spinner v-if="refreshing && !loading" small variant="primary" class="ml-50" v-b-tooltip.noninteractive.hover.bottom title="Arka planda güncelleniyor"></b-spinner>
            </div>
            <div class="dashboard-criticality-card__actions">
                <button
                    v-b-tooltip.noninteractive.hover.bottom
                    title="Yenile"
                    class="btn btn-sm btn-flat-secondary btn-icon"
                    :disabled="loading || refreshing"
                    v-on:click="refresh">
                    <b-spinner small v-if="loading || refreshing"></b-spinner>
                    <feather-icon v-else icon="RefreshCwIcon" size="14"></feather-icon>
                </button>
                <button
                    v-b-tooltip.noninteractive.hover.bottom
                    title="Excel'e Aktar"
                    class="btn btn-sm btn-flat-success btn-icon ml-25"
                    :disabled="loading || !filteredItems.length"
                    v-on:click="exportExcel">
                    <b-spinner small v-if="excelLoading"></b-spinner>
                    <b-icon-file-earmark-spreadsheet-fill v-else></b-icon-file-earmark-spreadsheet-fill>
                </button>
            </div>
        </div>

        <div v-if="visibleStats.length" class="dashboard-criticality-card__stats">
            <div
                v-for="s in visibleStats"
                :key="s.key"
                :class="['dashboard-stat', 'dashboard-stat--' + s.variant, { 'dashboard-stat--muted': !s.count }]"
                v-b-tooltip.noninteractive.hover.bottom
                :title="s.label">
                <span class="dashboard-stat__dot"></span>
                <span class="dashboard-stat__label text-truncate">{{ s.label }}</span>
                <span class="dashboard-stat__count">{{ s.count }}</span>
            </div>
        </div>

        <div class="dashboard-criticality-card__body">
            <b-overlay :show="loading" opacity="0.4" rounded="sm" class="dashboard-criticality-card__overlay">
                <div class="dashboard-criticality-card__scroll">
                    <table class="table table-sm dashboard-criticality-table mb-0">
                        <thead>
                            <tr>
                                <th
                                    v-for="f in fields"
                                    :key="f.key"
                                    :class="['dashboard-criticality-th', f.thClass]">
                                    {{ f.name }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="(item, idx) in filteredItems"
                                :key="idx"
                                :class="rowClassFor(item)">
                                <td
                                    v-for="f in fields"
                                    :key="f.key"
                                    :class="['dashboard-criticality-td', f.tdClass]">
                                    {{ formatValue(getCell(item, f.key)) }}
                                </td>
                            </tr>
                            <tr v-if="!loading && !filteredItems.length">
                                <td :colspan="fields.length" class="dashboard-criticality-empty">
                                    <feather-icon icon="InboxIcon" size="24" class="text-muted mb-50" />
                                    <div class="text-muted">{{ emptyText }}</div>
                                    <small v-if="hiddenByFilterCount" class="text-warning d-block mt-25">
                                        {{ hiddenByFilterCount }} kayıt aktif kritiklik filtresi nedeniyle gizlendi.
                                    </small>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </b-overlay>
        </div>
    </b-card>
</template>

<script>
import { BCard, BBadge, BOverlay, BSpinner, VBTooltip, BIconFileEarmarkSpreadsheetFill } from "bootstrap-vue";
import xlsx from "json-as-xlsx";
import objectPath from "object-path";

const DEFAULT_STATS_CONFIG = [
    { key: "danger", label: "Süresi Geçmiş / Kritik", variant: "danger" },
    { key: "warning", label: "Süresi Yaklaşan", variant: "warning" },
    { key: "secondary", label: "Diğer / Beklemede", variant: "secondary" },
];

const LEVEL_PRIORITY = { danger: 0, warning: 1, secondary: 2, info: 3, primary: 4, none: 99 };

const DEFAULT_SORT_KEYS = ["RemainingDate", "DaysRemaining"];

const CACHE_NAMESPACE = "tms.dashboardCache.v1.";

export default {
    name: "DashboardCriticalityCard",
    components: { BCard, BBadge, BOverlay, BSpinner, BIconFileEarmarkSpreadsheetFill },
    directives: { "b-tooltip": VBTooltip },
    props: {
        title: { type: String, required: true },
        fields: { type: Array, required: true },
        provider: { type: Function, required: true },
        getLevel: {
            type: Function,
            default: (item) => {
                if (item?.Status === "B") return "danger";
                if (item?.Status === "Y") return "warning";
                if (item?.Status === "DU") return "secondary";
                return null;
            },
        },
        sortValue: { type: Function, default: null },
        statsConfig: { type: Array, default: () => DEFAULT_STATS_CONFIG },
        enabledLevels: { type: Array, default: null },
        excelFileName: { type: String, default: "dashboard" },
        emptyText: { type: String, default: "Bu kriterlere uygun kayıt bulunamadı." },
    },
    data() {
        return {
            items: [],
            loading: false,
            refreshing: false,
            excelLoading: false,
        };
    },
    computed: {
        cacheKey() {
            return CACHE_NAMESPACE + this.excelFileName;
        },
        itemsWithLevel() {
            return (this.items || []).map((it) => ({ ...it, __level: this.getLevel(it) || null }));
        },
        sortedItems() {
            const extract = this.sortValue || this.defaultSortValue;
            return [...this.itemsWithLevel].sort((a, b) => {
                const la = LEVEL_PRIORITY[a.__level || "none"] ?? 99;
                const lb = LEVEL_PRIORITY[b.__level || "none"] ?? 99;
                if (la !== lb) return la - lb;
                const va = extract(a);
                const vb = extract(b);
                const aValid = Number.isFinite(va);
                const bValid = Number.isFinite(vb);
                if (!aValid && !bValid) return 0;
                if (!aValid) return 1;
                if (!bValid) return -1;
                return va - vb;
            });
        },
        filteredItems() {
            if (!Array.isArray(this.enabledLevels)) return this.sortedItems;
            return this.sortedItems.filter((it) => this.enabledLevels.includes(it.__level || "none"));
        },
        hiddenByFilterCount() {
            return this.sortedItems.length - this.filteredItems.length;
        },
        visibleStats() {
            return this.statsConfig.map((cfg) => ({
                ...cfg,
                count: this.itemsWithLevel.filter((it) => it.__level === cfg.key).length,
            }));
        },
    },
    mounted() {
        const hadCache = this.loadFromCache();
        this.refresh({ silent: hadCache });
    },
    methods: {
        defaultSortValue(item) {
            for (const k of DEFAULT_SORT_KEYS) {
                const v = item?.[k];
                if (v !== null && v !== undefined && v !== "") {
                    const n = Number(v);
                    if (Number.isFinite(n)) return n;
                }
            }
            return Number.POSITIVE_INFINITY;
        },
        getCell(item, key) {
            return objectPath.get(item, key);
        },
        formatValue(v) {
            if (v === null || v === undefined || v === "") return "-";
            return v;
        },
        rowClassFor(item) {
            if (!item?.__level) return "dashboard-criticality-row";
            return "dashboard-criticality-row dashboard-criticality-row--" + item.__level;
        },
        loadFromCache() {
            try {
                const raw = sessionStorage.getItem(this.cacheKey);
                if (!raw) return false;
                const cached = JSON.parse(raw);
                if (cached && Array.isArray(cached.data) && cached.data.length) {
                    this.items = cached.data;
                    return true;
                }
            } catch (e) {
                /* ignore corrupt cache */
            }
            return false;
        },
        writeCache(data) {
            try {
                sessionStorage.setItem(this.cacheKey, JSON.stringify({ ts: Date.now(), data }));
            } catch (e) {
                /* storage unavailable or quota exceeded */
            }
        },
        refresh({ silent = false } = {}) {
            if (silent) this.refreshing = true;
            else this.loading = true;
            return Promise.resolve(this.provider())
                .then((res) => {
                    const payload = res?.data?.Data ?? res?.data ?? res;
                    const data = Array.isArray(payload) ? payload : [];
                    this.items = data;
                    this.writeCache(data);
                })
                .catch(() => {
                    if (!silent) this.items = [];
                })
                .finally(() => {
                    this.loading = false;
                    this.refreshing = false;
                });
        },
        exportExcel() {
            if (this.excelLoading) return;
            const data = this.filteredItems;
            if (!data.length) return;
            this.excelLoading = true;
            try {
                const columns = this.fields.map((f) => ({
                    label: f.name,
                    value: (row) => {
                        const v = objectPath.get(row, f.key);
                        return v === null || v === undefined ? "" : v;
                    },
                }));
                const settings = {
                    fileName: `${this.excelFileName}_${new Date().toISOString().slice(0, 10)}`,
                    extraLength: 3,
                    writeMode: "writeFile",
                };
                xlsx([{ sheet: this.title.substring(0, 30), columns, content: data }], settings);
            } finally {
                this.excelLoading = false;
            }
        },
    },
};
</script>

<style>
.dashboard-criticality-card {
    height: 100%;
    display: flex;
    flex-direction: column;
    border: 1px solid #ebe9f1;
    border-radius: 0.428rem;
    overflow: hidden;
}

.dashboard-criticality-card__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.55rem 0.85rem;
    border-bottom: 1px solid #ebe9f1;
    gap: 0.5rem;
    flex-shrink: 0;
    background: #fff;
}

.dashboard-criticality-card__title-wrap {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    min-width: 0;
    flex: 1 1 auto;
}

.dashboard-criticality-card__title {
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
}

.dashboard-criticality-card__count {
    font-weight: 600;
    flex-shrink: 0;
}

.dashboard-criticality-card__actions {
    display: flex;
    align-items: center;
    flex-shrink: 0;
}

.dashboard-criticality-card__stats {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
    padding: 0.5rem 0.85rem;
    border-bottom: 1px solid #ebe9f1;
    flex-shrink: 0;
    background: #fafafc;
}

.dashboard-stat {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.18rem 0.55rem;
    border-radius: 999px;
    font-size: 0.72rem;
    font-weight: 600;
    line-height: 1.2;
    max-width: 100%;
    min-width: 0;
}

.dashboard-stat__dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
}

.dashboard-stat__label {
    max-width: 150px;
}

.dashboard-stat__count {
    font-weight: 700;
    padding-left: 0.3rem;
    margin-left: 0.05rem;
    border-left: 1px solid currentColor;
    opacity: 0.9;
}

.dashboard-stat--danger { background: rgba(220, 38, 38, 0.16); color: #b91c1c; }
.dashboard-stat--danger .dashboard-stat__dot { background: #dc2626; }

.dashboard-stat--warning { background: rgba(255, 159, 67, 0.14); color: #b86b15; }
.dashboard-stat--warning .dashboard-stat__dot { background: #ff9f43; }

.dashboard-stat--secondary { background: rgba(108, 117, 125, 0.14); color: #6c757d; }
.dashboard-stat--secondary .dashboard-stat__dot { background: #6c757d; }

.dashboard-stat--info { background: rgba(0, 207, 232, 0.14); color: #0c8fa5; }
.dashboard-stat--info .dashboard-stat__dot { background: #00cfe8; }

.dashboard-stat--primary { background: rgba(115, 103, 240, 0.14); color: #7367f0; }
.dashboard-stat--primary .dashboard-stat__dot { background: #7367f0; }

.dashboard-stat--muted { opacity: 0.5; }

.dashboard-criticality-card__body {
    flex: 1 1 0;
    min-height: 0;
    display: flex;
    overflow: hidden;
}

.dashboard-criticality-card__overlay,
.dashboard-criticality-card__body > .b-overlay-wrap {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    width: 100%;
}

.dashboard-criticality-card__scroll {
    width: 100%;
    height: 100%;
    overflow: auto;
    -webkit-overflow-scrolling: touch;
}

.dashboard-criticality-table {
    margin-bottom: 0;
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
}

.dashboard-criticality-th {
    position: sticky;
    top: 0;
    z-index: 2;
    background: #f3f2f7;
    color: #5e5873;
    font-weight: 600;
    font-size: 0.78rem;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    padding: 0.55rem 0.75rem;
    border-bottom: 1px solid #ebe9f1 !important;
    white-space: nowrap;
    text-align: left;
}

.dashboard-criticality-td {
    padding: 0.45rem 0.75rem;
    font-size: 0.85rem;
    color: #5e5873;
    border-top: 1px solid #f3f2f7;
    white-space: nowrap;
    vertical-align: middle;
}

.dashboard-criticality-row--danger { background-color: rgba(220, 38, 38, 0.18); }
.dashboard-criticality-row--danger .dashboard-criticality-td { color: #991b1b; font-weight: 500; }
.dashboard-criticality-row--danger:hover { background-color: rgba(220, 38, 38, 0.26); }

.dashboard-criticality-row--warning { background-color: rgba(255, 159, 67, 0.12); }
.dashboard-criticality-row--warning .dashboard-criticality-td { color: #8a5114; }
.dashboard-criticality-row--warning:hover { background-color: rgba(255, 159, 67, 0.18); }

.dashboard-criticality-row--secondary { background-color: rgba(108, 117, 125, 0.10); }
.dashboard-criticality-row--secondary .dashboard-criticality-td { color: #565d63; }
.dashboard-criticality-row--secondary:hover { background-color: rgba(108, 117, 125, 0.16); }

.dashboard-criticality-row--info { background-color: rgba(0, 207, 232, 0.10); }
.dashboard-criticality-row--info .dashboard-criticality-td { color: #0c8fa5; }

.dashboard-criticality-row:not([class*="--"]):hover { background-color: rgba(115, 103, 240, 0.05); }

.dashboard-criticality-empty {
    text-align: center;
    padding: 2rem 1rem !important;
    background: transparent !important;
}

.dark-layout .dashboard-criticality-card { border-color: #3b4253; }
.dark-layout .dashboard-criticality-card__header,
.dark-layout .dashboard-criticality-card__stats { border-bottom-color: #3b4253; }
.dark-layout .dashboard-criticality-card__header { background: #283046; }
.dark-layout .dashboard-criticality-card__stats { background: #242b3d; }
.dark-layout .dashboard-criticality-card__title { color: #d0d2d6; }
.dark-layout .dashboard-criticality-th { background: #343d55; color: #d0d2d6; border-bottom-color: #3b4253 !important; }
.dark-layout .dashboard-criticality-td { color: #b4b7bd; border-top-color: #3b4253; }

.dark-layout .dashboard-criticality-row--danger { background-color: rgba(220, 38, 38, 0.28); }
.dark-layout .dashboard-criticality-row--danger .dashboard-criticality-td { color: #fecaca; }
.dark-layout .dashboard-criticality-row--warning { background-color: rgba(255, 159, 67, 0.18); }
.dark-layout .dashboard-criticality-row--warning .dashboard-criticality-td { color: #ffd6a8; }
.dark-layout .dashboard-criticality-row--secondary { background-color: rgba(108, 117, 125, 0.22); }
.dark-layout .dashboard-criticality-row--secondary .dashboard-criticality-td { color: #cfd2d6; }

@media (max-width: 575.98px) {
    .dashboard-criticality-card__header { padding: 0.5rem 0.65rem; }
    .dashboard-criticality-card__title { font-size: 0.9rem; }
    .dashboard-criticality-card__stats { padding: 0.4rem 0.65rem; gap: 0.3rem; }
    .dashboard-stat { font-size: 0.68rem; padding: 0.14rem 0.45rem; }
    .dashboard-stat__label { max-width: 110px; }
    .dashboard-criticality-th { font-size: 0.72rem; padding: 0.45rem 0.55rem; }
    .dashboard-criticality-td { font-size: 0.8rem; padding: 0.4rem 0.55rem; }
}
</style>
